import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

export type ThemeMode = "light" | "dark";

export interface ThemeTokens {
  /** Window content background */
  windowBg: string;
  /** Optional CSS for subtle texture overlay */
  windowBgImage: string;
  /** Primary text */
  text: string;
  /** Headings */
  heading: string;
  /** Body / secondary text */
  body: string;
  /** Muted text */
  muted: string;
  /** Subtle / disabled text */
  subtle: string;
  /** Accent color (labels, links, tech chips) */
  accent: string;
  /** Accent border */
  accentBorder: string;
  /** Subtle border */
  border: string;
  /** Card / inset background */
  cardBg: string;
  /** Image placeholder background */
  placeholderBg: string;
  /** Input background */
  inputBg: string;
  /** Input border */
  inputBorder: string;
  /** Input text */
  inputText: string;
  /** Tag pill background */
  tagBg: string;
  /** Tag pill border */
  tagBorder: string;
  /** Tag pill text */
  tagText: string;
  /** Inline code background */
  codeBg: string;
  /** Success/live green */
  success: string;
  /** Muted success green */
  successMuted: string;
}

const lightTokens: ThemeTokens = {
  windowBg: "rgb(236, 232, 227)",
  windowBgImage:
    "url(\"data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")",
  text: "#000",
  heading: "#000",
  body: "#000",
  // `muted` is for inline meta (e.g. "(provider)" suffix). Still readable.
  muted: "#333",
  // `subtle` reserved for disabled-button text + true preview/placeholder text.
  // Standard "disabled grey" — readable but visibly inactive.
  subtle: "#6b7280",
  accent: "#006060",
  accentBorder: "#006060",
  border: "rgba(0,0,0,0.12)",
  cardBg: "rgba(0,0,0,0.03)",
  placeholderBg: "#e0dbd4",
  inputBg: "#fff",
  inputBorder: "rgba(0,0,0,0.2)",
  inputText: "#2c2c2c",
  tagBg: "rgba(0,96,96,0.08)",
  tagBorder: "rgba(0,96,96,0.18)",
  tagText: "#006060",
  codeBg: "rgba(0,0,0,0.06)",
  success: "#1a7a1a",
  successMuted: "rgba(26,122,26,0.5)",
};

const darkTokens: ThemeTokens = {
  windowBg: "#0a0a14",
  windowBgImage:
    "url(\"data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
  text: "#e0e0e0",
  heading: "#fff",
  body: "rgba(255,255,255,0.8)",
  muted: "rgba(255,255,255,0.5)",
  subtle: "rgba(255,255,255,0.3)",
  accent: "#4a9a9a",
  accentBorder: "rgba(74,154,154,0.3)",
  border: "rgba(255,255,255,0.25)",
  cardBg: "rgba(0,0,30,0.8)",
  placeholderBg: "#050510",
  inputBg: "#050510",
  inputBorder: "rgba(74,154,154,0.3)",
  inputText: "#e0e0e0",
  tagBg: "rgba(74,154,154,0.15)",
  tagBorder: "rgba(74,154,154,0.3)",
  tagText: "#4a9a9a",
  codeBg: "rgba(255,255,255,0.08)",
  success: "#0f0",
  successMuted: "rgba(0,255,0,0.5)",
};

interface ThemeContextValue {
  mode: ThemeMode;
  tokens: ThemeTokens;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: "light",
  tokens: lightTokens,
  toggle: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Default to light (readability rule: black text on light bg).
  // Returning visitors get whichever mode they previously chose.
  const [mode, setMode] = useState<ThemeMode>("light");

  useEffect(() => {
    const saved = localStorage.getItem("repancy-theme") as ThemeMode | null;
    if (saved && saved !== mode) setMode(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("repancy-theme", next);
      return next;
    });
  }, []);

  const tokens = mode === "light" ? lightTokens : darkTokens;

  return (
    <ThemeContext.Provider value={{ mode, tokens, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
